import React, { useState, useMemo, useEffect } from "react";
import { MdOutlineAdd, MdOutlineEdit } from "react-icons/md";
import { FcAbout } from "react-icons/fc";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, Button, Input } from "reactstrap";
import { useTranslation } from "react-i18next";
import { getUserRolePermissions, getUsers } from "../../services/userServicer";
import i18n from "../../i18n";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./userManage.css";
import { getInstitutions } from "../../services/institutions";
import LoadingScreen from "../../components/Loading/LoadingScreen";

interface UserData {
  id: string;
  code: string;
  username: string;
  name: string;
  email: string;
  role: string;
  institution: string;
  index: number;
}

const UserManage: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<UserData[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const navigate = useNavigate();
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [rowId, setRowId] = useState<string>("");
  const [rowStatus, setRowStatus] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInstitution, setSelectedInstitution] = useState<string>("");
  const [institutions, setInstitutions] = useState<{ name: string; id: string }[]>([]);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof UserData | "index";
    direction: "ascending" | "descending" | null;
  }>({ key: "index", direction: "ascending" });

  const [userPermissions, setUserPermissions] = useState<any>(null);
  const [notAllowed, setNotAllowed] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchUsersData = async (page: number) => {
    setLoading(true);
    try {
      const response: any = await getUsers("", perPage, page);
      
      if (response && response.success) {
        const indexedData = response.data.map((user: UserData, idx: number) => ({
          ...user,
          index: idx + 1 + page * perPage,
        }));
        setData(indexedData);
        setTotalPages(Math.ceil(response.totalCount / perPage));
      } else if (response && response.data.length === 0) {
        setData([]);
        setTotalPages(0);
      } else {
        toast.error("Failed to fetch users data");
      }
    } catch (error: any) {
      toast.error("Error fetching users data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInstitutions = async () => {
    try {
      const response = await getInstitutions("", 1000, 0);
      if (response.success) {
        setInstitutions(response.data);
      }
    } catch (error: any) {
      toast.error(error.message);
      console.log(error);
    }
  };

  useEffect(() => {
    // const fetchUserRolePermissions = async () => {

    //   try {
    //     let userId: any = localStorage.getItem('userId');
    //     const permissions = await getUserRolePermissions(userId);
    //     if (permissions.success) {
    //       const userPagePermission = permissions.data.permissions.find((permission: any) => permission.page === "users");
    //       if (userPagePermission) {
    //         setUserPermissions(userPagePermission.permissions);

    //         if (userPagePermission.permissions.show) {
    //           setNotAllowed(false);
    //         } else {
    //           setNotAllowed(true);
    //         }
    //       }
    //     }
    //   } catch (error: any) {
    //     toast.error(t("permissions.Error_fetching_user_role_permissions") + ":", error);
    //   }
    // }
    // fetchUserRolePermissions();
    fetchUsersData(currentPage);
    fetchInstitutions();
  }, [perPage, currentPage]);

  const sortedData = useMemo(() => {
    let sortableData = [...data];
    if (sortConfig.direction !== null) {
      sortableData.sort((a: any, b: any) => {
        if (sortConfig.key === "index") {
          return sortConfig.direction === "ascending" ? a.index - b.index : b.index - a.index;
        }
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig, currentPage, perPage]);

  const filteredData = useMemo(() => {
    const filtered = sortedData.filter((item) => {
      const matchesSearchTerm = (
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.institution?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      const matchesInstitution = selectedInstitution ? item.institution === selectedInstitution : true;

      return matchesSearchTerm && matchesInstitution;
    });
    return filtered;
  }, [sortedData, searchTerm, selectedInstitution, currentPage, perPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedInstitution]);

  const toggleSort = (key: keyof UserData | "index") => {
    let direction: "ascending" | "descending" | null = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    } else if (sortConfig.key === key && sortConfig.direction === "descending") {
      direction = null;
    }
    setSortConfig({ key, direction });
  };


  if (loading) {
    return (
      <div className="loading-screen" 
      style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.8)'}}>
                     <div><LoadingScreen/></div>
                   </div>
    );
  }

  return (
    <div className="root">
      
   
      <h4 className="mb-4 px-2">{t("User_Manage.page_title_user_manage")}</h4>
      <div className="container_info p-3 rounded " style={{  backgroundColor: 'var(--widget-bg)' }}>
        <div className="d-flex justify-content-between ">
        <div className="custom-select-wrapper ml-2" style={{paddingBottom:'20px'}}>
            <label htmlFor="institution-select">{t("User_Manage.select_institution")}</label>
            <select
              id="institution-select"
              value={selectedInstitution}
              style={{borderRadius:'10px',border:'1px solid #ccc',outline:'none'}}
              onChange={(e) => setSelectedInstitution(e.target.value)}
              className="custom-select"
            >
              <option value="">{t("User_Manage.all_institutions")}</option>
              {institutions.map((institution, index) => (
                <option key={index} value={institution.name}>{institution.name}</option>
              ))}
            </select>
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("User_Manage.search_placeholder") || ''}
            className="search-input"
          />
         
        </div> 
        </div>
        <div className="pt-3">
        <table className="styled-table">
          <thead>
            <tr>
              <th onClick={() => toggleSort("index")} className="sortable-header">
                #
                {sortConfig.key === "index"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("username")} className="sortable-header">
                {t("User_Manage.username")}{" "}
                {sortConfig.key === "username"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("name")} className="sortable-header">
                {t("User_Manage.name")}{" "}
                {sortConfig.key === "name"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("email")} className="sortable-header">
                {t("User_Manage.email")}{" "}
                {sortConfig.key === "email"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
              <th onClick={() => toggleSort("role")} className="sortable-header">
                {t("User_Manage.role")}{" "}
                {sortConfig.key === "role"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>

              <th onClick={() => toggleSort("institution")} className="sortable-header">
                {t("User_Manage.institute")}{" "}
                {sortConfig.key === "institution"
                  ? sortConfig.direction === "ascending"
                    ? "🔼"
                    : "🔽"
                  : ""}
              </th>
           
             
            </tr>
          </thead>
          {data.length > 0 && (
            <tbody>
              {filteredData.map((item: any, index) => (
                <React.Fragment key={index}>
                  <tr>
                    <td>{index + 1}</td>
                    <td>{item?.username}</td>
                    <td>{item?.name}</td>
                    <td>{item?.email}</td>
                    <td>{item?.role}</td>
                    <td>{item?.institution}</td>
                    
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          )}
          {data.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={7} className="text-center alert alert-warning">
                  {t("User_Manage.no_users_available")}
                </td>
              </tr>
            </tbody>
          )}
        </table>

        <div className="pagination-controls mt-3">
  <button
    className="btn btn-warning btn-sm text-white"
    onClick={() => setCurrentPage(0)}
    disabled={currentPage === 0}
  >
    {"<<"}
  </button>{" "}
  <button
    className="btn btn-warning btn-sm text-white"
    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
    disabled={currentPage === 0}
  >
    {"<"}
  </button>{" "}
  <button
    className="btn btn-warning btn-sm text-white"
    onClick={() =>
      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
    }
    disabled={currentPage+1 >= totalPages}
  >
    {">"}
  </button>{" "}
  <button
    className="btn btn-warning btn-sm text-white"
    onClick={() => setCurrentPage(totalPages)}
    disabled={currentPage+1 >= totalPages}
  >
    {">>"}
  </button>{" "}
  <span>
    {t("User_Manage.page")} {currentPage+1} {t("User_Manage.to")} {totalPages}
  </span>
</div>
      
      </div>

       <div className="container_info">
              {/* <div className="d-flex justify-content-between my-2 text-center alert alert-danger">
                <h5 style={{width:'100%'}} className="text-center">{t("permissions.not_allowed")}</h5>
              </div> */}
            </div>
    </div>
  );
};

export default UserManage;
